import React from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

import type { Color } from '#/ui/types';
import type { OverridableStringUnion, OverrideProps } from '#/ui/utils/types';
import Box from './box';

export type TypographyPropsVariantOverrides = TF.EmptyObject;

export type TypographyPropsColorOverrides = TF.EmptyObject;

export interface TypographyOwnProps {
  /**
   * The component used for the root node.
   * @default 'span'
   */
  component?: React.ElementType;
  /**
   * Set the text-align on the component.
   * @default 'inherit'
   */
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
  /**
   * The color of the component.
   */
  color?: OverridableStringUnion<Color | 'inherit', TypographyPropsColorOverrides>;
  /**
   * If `true`, the text will have a bottom margin.
   * @default false
   */
  gutterBottom?: boolean;
  /**
   * If `true`, the text will not wrap, but instead will truncate with a text overflow ellipsis.
   *
   * Note that text overflow can only happen with block or inline-block level elements
   * (the element needs to have a width in order to overflow).
   * @default false
   */
  noWrap?: boolean;
  /**
   * Applies the theme typography styles.
   * @default 'body1'
   */
  variant?: OverridableStringUnion<keyof typeof variantMapping, TypographyPropsVariantOverrides>;
  /**
   * If `true`, will use the contrast text color.
   * @default true
   */
  contrast?: boolean;
}

export interface TypographyTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'span',
> {
  props: AdditionalProps & TypographyOwnProps;
  defaultComponent: RootComponent;
}

export type TypographyProps<
  RootComponent extends React.ElementType = TypographyTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<TypographyTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

function Typography<C extends React.ElementType>({
  variant = 'body1',
  align = 'inherit',
  gutterBottom = false,
  color,
  contrast = true,
  className,
  children,
  ...props
}: TypographyProps) {
  const calcVariant = React.useMemo(() => variantMapping[variant], [variant]);
  const colorClassName = React.useMemo(() => {
    if (!color) {
      return calcVariant.color;
    }

    if (color && contrast) {
      return colorMapping[color].contrast;
    }

    return colorMapping[color].base;
  }, [color, calcVariant.color, contrast]);
  return (
    <Box
      {...props}
      component={calcVariant.component}
      className={twMerge(
        calcVariant.size,
        alignMapping[align],
        colorClassName,
        gutterBottom && 'mb-4',
        className
      )}
    >
      {children}
    </Box>
  );
}
Typography.displayName = 'GooeyTypography';

export default Typography;

/*
|------------------
| Internal
|------------------
*/

const alignMapping = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
  inherit: '',
};

const variantMapping = {
  h1: {
    size: 'text-4xl sm:text-5xl md:text-6xl',
    color: 'text-base-contrast',
    component: 'h1',
  },
  h2: {
    size: 'text-3xl sm:text-4xl md:text-5xl',
    color: 'text-base-contrast',
    component: 'h2',
  },
  h3: {
    size: 'text-2xl sm:text-3xl md:text-4xl',
    color: 'text-base-contrast',
    component: 'h3',
  },
  h4: {
    size: 'text-xl sm:text-2xl md:text-3xl',
    color: 'text-base-contrast',
    component: 'h4',
  },
  h5: {
    size: 'text-lg sm:text-2xl',
    color: 'text-base-contrast',
    component: 'h5',
  },
  h6: {
    size: 'text-lg sm:text-xl',
    color: 'text-base-contrast',
    component: 'h6',
  },
  subtitle1: {
    size: 'text-md sm:text-lg',
    color: 'text-base-contrast',
    component: 'p',
  },
  subtitle2: {
    size: 'text-md sm:text-lg',
    color: 'text-gray-400',
    component: 'p',
  },
  body1: {
    size: 'text-base',
    color: 'text-base-contrast',
    component: 'p',
  },
  body2: {
    size: 'text-base',
    color: 'text-gray-400',
    component: 'p',
  },
  caption: {
    size: 'text-xs',
    color: 'text-gray-400',
    component: 'span',
  },
} as const;

const colorMapping = {
  primary: {
    base: 'text-primary',
    contrast: 'text-primary-contrast',
  },
  secondary: {
    base: 'text-secondary',
    contrast: 'text-secondary-contrast',
  },
  success: {
    base: 'text-success',
    contrast: 'text-success-contrast',
  },
  accent: {
    base: 'text-accent',
    contrast: 'text-accent-contrast',
  },
  error: {
    base: 'text-error',
    contrast: 'text-error-contrast',
  },
  info: {
    base: 'text-info',
    contrast: 'text-info-contrast',
  },
  warning: {
    base: 'text-warning',
    contrast: 'text-warning-contrast',
  },
  inherit: {
    base: '',
    contrast: '',
  },
} as const;
